import { useCallback, useEffect, useRef, useState } from "react";
import { getAddress } from "viem";
import { nftSchema, redeemPreviewSchema, type BootstrapResponse } from "../../../api/schemas";
import { encodeRedeemCall, encodeRedeemWithFeesCall } from "../../../contracts/pons-reward-nft";
import { ContractReadError } from "../../../contracts/contract-errors";
import { parseUnsignedDecimal } from "../../../lib/financial";
import { redeemProtection, redeemWithFeesProtection } from "../../../lib/redeem-protection";
import type { RedeemFeeRoute } from "../../../wallet/redeem-fee-quote";
import { checkRedeemState, readSimulatedRedeemWithFeesPayout, simulateRedeemWithFeesReview } from "../../../wallet/redeem-checks";
import { matchesTransactionScope, transactionUnresolved, type TransactionContext, type TransactionTracker } from "../../../wallet/transaction-tracker";
import { ContextChangedError, type WalletOperation } from "../../../wallet/wallet-operation";
import type { WalletSessionController } from "../../../wallet/wallet-session";
import type { ActionContext } from "../../../state/action-context";
import { sameAddress } from "../../../state/model-helpers";
import { actionPermissions } from "../../../state/transaction-capability";
import type { RedeemReviewView } from "../types";
import { selectRedeemRoute, isDirectRedeemRoute, type RedeemRoute } from "./redeem-route";
import { ELYSIUM } from "../../../config/elysium";
import { readElysiumRedeemPreview, simulateElysiumRedeem } from "../../../wallet/elysium-redeem";
import { assertRedeemNftDetail } from "./redeem-nft-validation";
import { isProductionRedeemRouteEnabled } from "../../../wallet/redeem-fee-route";
import { createRedemptionDiagnostics } from "../../../wallet/redemption-diagnostics";

export function useRedeemNft({ walletController, bootstrap, tracker, transactionEnvironment, refresh, feeRoute = null, feeRouteUnavailable = null,
  contextKey, readContextKey, activeContext, beginOperation, endOperation }: ActionContext & {
  walletController: WalletSessionController; bootstrap: BootstrapResponse | null; tracker: TransactionTracker;
  transactionEnvironment: string; refresh(): void;
  feeRoute?: RedeemFeeRoute | null;
  feeRouteUnavailable?: string | null;
}) {
  const live = useRef({ bootstrap, feeRoute, feeRouteUnavailable });
  live.current = { bootstrap, feeRoute, feeRouteUnavailable };
  const pending = useRef<{ view: RedeemReviewView; operation: WalletOperation; context: TransactionContext; route: RedeemRoute | null } | null>(null);
  const preparing = useRef<WalletOperation | null>(null);
  const [review, setReview] = useState<RedeemReviewView | null>(null);
  const cancelRedeemReview = useCallback(() => {
    for (const operation of [pending.current?.operation, preparing.current]) {
      if (operation) { operation.cancel(); endOperation(operation); }
    }
    pending.current = null;
    preparing.current = null;
    setReview(null);
  }, [endOperation]);
  const routeKey = JSON.stringify([feeRoute, feeRouteUnavailable]);
  const permissionKey = JSON.stringify([bootstrap?.capabilities, bootstrap?.deployment, bootstrap?.contracts, bootstrap?.environment, bootstrap?.forkTestOnly]);
  useEffect(() => () => cancelRedeemReview(), [contextKey, routeKey, permissionKey, cancelRedeemReview]);

  const currentPreview = useCallback(async (context: TransactionContext, operation: WalletOperation, route: RedeemRoute | null,
    diagnostics = createRedemptionDiagnostics(operation.signal)) => {
    if (isDirectRedeemRoute(route)) {
      const provider = walletController.getProvider();
      if (!provider) throw new ContextChangedError();
      const result = await readElysiumRedeemPreview(provider, { account: getAddress(context.account), tokenId: BigInt(context.tokenId!),
        minimum: 0n, route }, operation.assertCurrent, { signal: operation.signal, diagnostics });
      return { payout: result.payout, quote: null };
    }
    const detail = await diagnostics.step("Read NFT detail", () => walletController.api.get(`/nfts/${context.tokenId}`, nftSchema, operation.signal));
    operation.assertCurrent();
    assertRedeemNftDetail(detail, context);
    if (route) {
      const provider = walletController.getProvider();
      if (!provider) throw new ContextChangedError();
      const quote = await diagnostics.step("Read fee redemption quote", () => checkRedeemState(provider, { account: context.account, tokenId: BigInt(context.tokenId!),
        chainId: context.chainId, collection: getAddress(context.collection), sourceToken: context.sourceToken,
        minimumPayoutWei: 0n, feeRoute: route, contextRevision: readContextKey() }, operation.assertCurrent, { signal: operation.signal }));
      if (!quote) throw new Error("Sweep quote unavailable");
      return { payout: quote.payoutWei, quote };
    }
    const preview = await diagnostics.step("Read legacy redemption preview", () => walletController.api.get(`/nfts/${context.tokenId}/redeem-preview`, redeemPreviewSchema, operation.signal));
    operation.assertCurrent();
    if (!preview.available || !preview.redeemable || !preview.expectedPayoutWei) {
      throw new Error(typeof preview.reason === "string" ? preview.reason : "A fresh redeemable payout preview is unavailable.");
    }
    if (!sameAddress(preview.sourceToken, context.sourceToken)) throw new Error("Redemption preview source token mismatch");
    const payout = parseUnsignedDecimal(preview.expectedPayoutWei, "expected payout");
    if (payout <= 0n || payout >= 1n << 256n) throw new Error("PayoutIsZero: A positive payout is required.");
    return { payout, quote: null };
  }, [walletController, readContextKey]);

  const prepareRedeem = useCallback(async (tokenId: string) => {
    if (preparing.current) throw new Error("Redemption preparation is already in progress.");
    cancelRedeemReview();
    if (contextKey !== readContextKey()) throw new ContextChangedError();
    const permission = actionPermissions(bootstrap).redeem;
    if (permission.state !== "ALLOWED") throw new Error(permission.reasons.join(", "));
    const collection = bootstrap?.contracts.collectionAddress;
    const chainId = bootstrap?.contracts.mintChainId;
    const account = walletController.getAccount();
    const sourceToken = activeContext.current.token;
    if (!collection || !chainId || !account || !sourceToken || !walletController.getProvider()) throw new Error("Connect the current NFT owner wallet and select the source token.");
    if (chainId !== ELYSIUM.chainId && !walletController.getSnapshot().authenticated) throw new Error("Sign in to request a fresh redemption preview.");
    const id = parseUnsignedDecimal(tokenId, "token ID");
    if (id.toString() !== tokenId || id >= 1n << 256n) throw new Error("Invalid NFT token ID");
    const context: TransactionContext = { kind: "REDEEM", account, chainId, collection, sourceToken, tokenId, environment: transactionEnvironment };
    const route = selectRedeemRoute(bootstrap, sourceToken, feeRoute);
    const sweep = route?.kind === "REDEEM_WITH_FEES_CURVE_V1";
    if (feeRouteUnavailable) throw new Error(feeRouteUnavailable);
    if (route && !isDirectRedeemRoute(route) && route.support === "PINNED_RPC_READ_ONLY" && !isProductionRedeemRouteEnabled(route, bootstrap)) {
      throw new Error("Live fee redemption is disabled by the current bootstrap configuration.");
    }
    if (tracker.redeemed(context)) throw new Error("Redemption and burn are already verified. Wait for inventory indexing.");
    if (tracker.getSnapshot().items.some((item) => matchesTransactionScope(item, context) && transactionUnresolved(item))) {
      throw new Error("Check the existing transaction before preparing another redemption.");
    }
    const operation = beginOperation();
    preparing.current = operation;
    const diagnostics = createRedemptionDiagnostics(operation.signal);
    try {
      await diagnostics.step("Prepare redemption", async () => {
        let prepared: { expected: bigint; quote: Awaited<ReturnType<typeof currentPreview>>["quote"];
          reviewedAt: number; protection: { minimumPayoutWei: bigint; deadline: number } } | null = null;
        for (let attempt = 0; attempt < (sweep ? 2 : 1); attempt++) {
          const { payout: estimated, quote } = await currentPreview(context, operation, route, diagnostics);
          const reviewedAt = Math.floor(Date.now() / 1_000);
          let expected = estimated;
          if (sweep) {
            if (!quote) throw new Error("Sweep quote unavailable");
            const provider = walletController.getProvider();
            if (!provider) throw new ContextChangedError();
            expected = await diagnostics.step("Simulate fee payout", () => readSimulatedRedeemWithFeesPayout(provider, { account, chainId, collection, tokenId: id,
              deadline: BigInt(reviewedAt + 600), blockNumber: quote.provenance.blockNumber },
            operation.assertCurrent, { signal: operation.signal }));
          }
          const protection = sweep ? redeemWithFeesProtection(expected, reviewedAt) : redeemProtection(expected, reviewedAt);
          if (isDirectRedeemRoute(route)) {
            const provider = walletController.getProvider();
            if (!provider) throw new ContextChangedError();
            await simulateElysiumRedeem(provider, { account, tokenId: id, minPayoutWei: protection.minimumPayoutWei,
              deadline: BigInt(protection.deadline) }, operation.assertCurrent, { signal: operation.signal, diagnostics });
          }
          if (sweep) {
            const provider = walletController.getProvider();
            if (!provider) throw new ContextChangedError();
            try {
              await diagnostics.step("Simulate fee redemption", () => simulateRedeemWithFeesReview(provider, { account, chainId, collection, tokenId: id,
                minimumPayoutWei: protection.minimumPayoutWei, deadline: BigInt(protection.deadline) },
              operation.assertCurrent, { signal: operation.signal }));
            }
            catch (error) {
              if (error instanceof ContractReadError && error.errorName === "PayoutBelowMinimum") {
                if (attempt === 0) continue;
                throw new Error(`Fresh redemption quote disagrees with contract simulation at quote block ${quote?.provenance.blockNumber}. ${error.message} No transaction was sent. Check the wallet RPC before trying again.`);
              }
              throw error;
            }
          }
          prepared = { expected, quote, reviewedAt, protection };
          break;
        }
        if (!prepared) throw new Error("A simulation-checked redemption quote is unavailable.");
        const { expected, quote, reviewedAt, protection } = prepared;
        operation.assertCurrent();
        if (actionPermissions(live.current.bootstrap).redeem.state !== "ALLOWED") throw new ContextChangedError();
        const view: RedeemReviewView = { id: crypto.randomUUID(), contextKey, tokenId, account, sourceToken, chainId, collection,
          expectedPayoutWei: expected.toString(), minimumPayoutWei: protection.minimumPayoutWei.toString(), deadline: protection.deadline,
          route: isDirectRedeemRoute(route) ? "DIRECT" : sweep ? "WITH_FEES" : "LEGACY", reviewedAt,
          ...(quote ? { vaultPoolWei: quote.vaultPoolWei.toString(), poolAfterSweepWei: quote.poolAfterSweepWei.toString(),
            quoteBlockNumber: quote.provenance.blockNumber.toString(), quoteBlockHash: quote.provenance.blockHash,
            ...(quote.payoutWei !== expected ? { feeEstimatedPayoutWei: quote.payoutWei.toString() } : {}) } : {}) };
        pending.current = { view, operation, route, context: { ...context, redeemMinimumWei: view.minimumPayoutWei,
          ...(route ? { redeemDeadline: view.deadline, redeemRouteRevision: route.deploymentRevision } : {}) } };
        setReview(view);
      });
    } catch (error) { operation.cancel(); endOperation(operation); throw error; }
    finally { if (preparing.current === operation) preparing.current = null; }
  }, [walletController, bootstrap, tracker, transactionEnvironment, contextKey, readContextKey, activeContext,
    beginOperation, endOperation, cancelRedeemReview, currentPreview, feeRoute, feeRouteUnavailable]);

  const confirmRedeem = useCallback(async (reviewId: string) => {
    const saved = pending.current;
    if (!saved || saved.view.id !== reviewId) throw new Error("Prepare and review redemption first.");
    pending.current = null;
    setReview(null);
    const { view, context, operation, route } = saved;
    const sweep = route?.kind === "REDEEM_WITH_FEES_CURVE_V1";
    const minimum = BigInt(view.minimumPayoutWei);
    try {
      operation.assertCurrent();
      const provider = walletController.getProvider();
      if (!provider) throw new ContextChangedError();
      const result = await tracker.execute(context, provider, async () => {
        operation.assertCurrent();
        const current = live.current;
        if (actionPermissions(current.bootstrap).redeem.state !== "ALLOWED" || current.feeRouteUnavailable ||
          JSON.stringify(selectRedeemRoute(current.bootstrap, context.sourceToken, current.feeRoute)) !== JSON.stringify(route)) throw new ContextChangedError();
        if (Math.floor(Date.now() / 1_000) >= view.deadline) throw new Error("Redemption quote expired. Prepare and review again.");
        const { payout: estimated, quote } = await currentPreview(context, operation, route);
        if (sweep && !quote) throw new Error("Sweep quote unavailable");
        const payout = sweep && quote ? await readSimulatedRedeemWithFeesPayout(provider, {
          account: context.account, chainId: context.chainId, collection: context.collection,
          tokenId: BigInt(context.tokenId!), deadline: BigInt(view.deadline), blockNumber: quote.provenance.blockNumber,
        }, operation.assertCurrent, { signal: operation.signal }) : estimated;
        if (payout < minimum) throw new Error(route ?
          `PayoutBelowMinimum: Current simulated payout is ${payout} wei, below reviewed minimum ${minimum} wei. No transaction was sent. Prepare and review a new quote; the minimum was not lowered.` :
          "PayoutBelowMinimum: Prepare and review a new quote; the minimum was not lowered.");
        // The fee reader already checks the execution network before quoting.
        // Keep the isolated legacy switch behavior; never retarget a fee quote.
        if (!route) {
          await operation.switchTo(view.chainId);
          await checkRedeemState(provider, { account: view.account, chainId: view.chainId, collection: getAddress(view.collection),
            tokenId: BigInt(view.tokenId), sourceToken: view.sourceToken, minimumPayoutWei: minimum },
            operation.assertCurrent, { signal: operation.signal });
        }
        const args = { tokenId: BigInt(view.tokenId), minPayoutWei: minimum, deadline: BigInt(view.deadline) };
        return { account: getAddress(view.account), chainId: view.chainId, to: getAddress(view.collection), valueWei: 0n,
          data: sweep ? encodeRedeemWithFeesCall(args) : encodeRedeemCall(args),
          ...(route && !isDirectRedeemRoute(route) ? { redeemIdentity: { ...args, reviewedAt: BigInt(view.reviewedAt!), route,
            getBootstrap: () => live.current.bootstrap } } : {}),
          ...(isDirectRedeemRoute(route) ? { directRedeemIdentity: { ...args, reviewedAt: BigInt(view.reviewedAt!), route,
            getBootstrap: () => live.current.bootstrap } } : {}) };
      }, operation);
      // execute records outcomes instead of rejecting on failure/rejection.
      // Propagate feedback without altering an uncertain broadcast or its lock.
      if (["FAILED", "REJECTED", "CANCELLED", "REVERTED"].includes(result.phase) ||
        !result.hash && result.phase === "UNCONFIRMED") throw new Error(result.message);
      if (contextKey === readContextKey()) refresh();
    } finally { endOperation(operation); }
  }, [walletController, tracker, currentPreview, endOperation, refresh, contextKey, readContextKey]);

  // Retain the model action signature for callers, without accepting an unreviewed minimum.
  const redeemNft = useCallback(async (tokenId: string, minimumPayoutWei: string) => {
    const view = pending.current?.view;
    if (!view || view.tokenId !== tokenId || view.minimumPayoutWei !== minimumPayoutWei) throw new Error("Prepare and review this redemption first.");
    await confirmRedeem(view.id);
  }, [confirmRedeem]);
  return { prepareRedeem, confirmRedeem, cancelRedeemReview, redeemNft,
    redeemReview: review?.contextKey === contextKey ? review : null };
}
